import { AP_PALETTE } from "@/core/colors";
/**
 * Wording and helpers for the `create_qbo_bill` / `update_qbo_bill` actions
 * and an invoice's QuickBooks bill, shared by the action labels, the review
 * card, the invoice header and the Invoices table so a bill reads the same
 * everywhere.
 */
import { formatCentsOrDash } from "@/core/utils/money";
import { AP_ACTION_TYPE } from "./types";
import type {
  ApActionType,
  ApAgentAction,
  ApQboBillPayload,
  ApQboBillPayloadEdit,
  ApQboBillVendor,
  ApQuickbooksBill,
  ApQuickbooksBillPushStatus,
  ApQuickbooksBillStatus,
} from "./types";
import type { StatusMeta } from "./utils";

export const QUICKBOOKS_BILL_STATUS: Record<
  ApQuickbooksBillStatus,
  StatusMeta
> = {
  open: {
    color: "success",
    label: "In QuickBooks",
    quiet: true,
  },
  paid: {
    color: "success",
    label: "Paid in QuickBooks",
    quiet: true,
  },
  // Gone from the books, so the invoice is owed a bill again: worth noticing.
  deleted: {
    color: "warning",
    label: "Deleted in QuickBooks",
  },
};

export const QUICKBOOKS_LINKED_EXISTING: StatusMeta = {
  color: AP_PALETTE.neutral.tone,
  label: "Linked to existing bill",
};

/** "Ferguson Enterprises", or "new vendor Ferguson" when the bill creates it. */
const vendorPhrase = (vendor: ApQboBillVendor) =>
  vendor.mode === "create"
    ? `new vendor ${vendor.display_name}`
    : vendor.display_name;

/** The two action types that write a bill to QuickBooks. */
export const isQboBillActionType = (type: ApActionType) =>
  type === AP_ACTION_TYPE.createQboBill ||
  type === AP_ACTION_TYPE.updateQboBill;

/**
 * "Create $157.10 bill for Ferguson Enterprises in QuickBooks", or "Update …"
 * for a re-push of a live bill.
 */
export const qboBillTitle = (
  payload: ApQboBillPayload,
  mode: "create" | "update" = "create",
) =>
  `${mode === "update" ? "Update" : "Create"} ${formatCentsOrDash(payload.total_cents)} bill for ${vendorPhrase(payload.vendor)} in QuickBooks`;

/** The title without its category: "$157.10 for Ferguson Enterprises". */
export const qboBillDetail = (payload: ApQboBillPayload) =>
  `${formatCentsOrDash(payload.total_cents)} for ${vendorPhrase(payload.vendor)}`;

/**
 * A nudge to check the vendor when the API guessed it: the closest name, or
 * no match at all (so a new vendor). Null when it was linked, matched exactly,
 * or chosen by a reviewer (the API sets "reviewer" on a saved edit; the mode
 * check covers an unsaved switch).
 */
export const qboBillVendorHint = ({
  vendor,
  vendor_match,
}: Pick<ApQboBillPayload, "vendor" | "vendor_match">): string | null => {
  if (vendor_match === "similar" && vendor.mode === "link")
    return "Closest name match in QuickBooks. Check it’s the right vendor.";
  if (vendor_match === "none" && vendor.mode === "create")
    return "No match found in QuickBooks. Check it isn’t already there under another name.";
  return null;
};

/**
 * Explains the balancing line the API adds when the invoice's parsed lines
 * don't sum to its printed total. Null when they do.
 */
export const qboBillUnreconciledNote = (
  payload: Pick<ApQboBillPayload, "unreconciled_cents">,
): string | null =>
  payload.unreconciled_cents
    ? `The invoice’s lines don’t add up to its total, so a ${formatCentsOrDash(payload.unreconciled_cents)} line was added to make the bill match.`
    : null;

const sameVendor = (a: ApQboBillVendor, b: ApQboBillVendor) =>
  a.mode === "link" && b.mode === "link"
    ? a.quickbooks_vendor_id === b.quickbooks_vendor_id
    : a.mode === b.mode && a.display_name.trim() === b.display_name.trim();

/**
 * The PATCH / approve body for an edited bill: the vendor and the expense
 * account, which are all the API reads for this action type, and each only
 * when it changed. Sending the vendor marks it as the reviewer's choice, and
 * re-checks it, so an account-only edit mustn't clear the "please check this
 * guess" hint or trip over a proposed vendor QuickBooks has since deactivated.
 * Empty when nothing changed.
 */
export const qboBillPayloadEdit = (
  draft: ApQboBillPayload,
  original: ApQboBillPayload,
): ApQboBillPayloadEdit => ({
  ...(!sameVendor(draft.vendor, original.vendor) && {
    vendor:
      draft.vendor.mode === "create"
        ? { mode: "create", display_name: draft.vendor.display_name.trim() }
        : draft.vendor,
  }),
  ...(draft.expense_account_id !== original.expense_account_id && {
    expense_account_id: draft.expense_account_id,
  }),
});

const QUICKBOOKS_BILL_NUMBER_MAX_LENGTH = 21;

export const quickbooksBillNumber = (
  invoiceNumber: string | null | undefined,
): string | null =>
  invoiceNumber?.trim()
    ? invoiceNumber.trim().slice(-QUICKBOOKS_BILL_NUMBER_MAX_LENGTH)
    : null;

/** A bill that is in QuickBooks and wasn't deleted there (the API's `live_bill?`). */
export const hasLiveQuickbooksBill = (
  bill: ApQuickbooksBill | null | undefined,
): bill is ApQuickbooksBill => !!bill && bill.status !== "deleted";

/**
 * Whether a push creates a bill or rewrites the live one. The API only says
 * while the invoice is pushable, so otherwise the invoice's own bill decides,
 * keeping a disabled button's label right.
 */
export const quickbooksPushMode = (
  status: Pick<ApQuickbooksBillPushStatus, "mode">,
  bill: ApQuickbooksBill | null | undefined,
): "create" | "update" =>
  status.mode ?? (hasLiveQuickbooksBill(bill) ? "update" : "create");

export const QUICKBOOKS_PUSH_LABEL: Record<"create" | "update", string> = {
  create: "Push to QuickBooks",
  update: "Update bill in QuickBooks",
};

/** The API's reasons are bare phrases; on screen they end like a sentence. */
const asSentence = (text: string) =>
  /[.!?]$/.test(text.trim()) ? text.trim() : `${text.trim()}.`;

/**
 * The muted line beside the push control: why it can't be pushed now, or,
 * when it can but has no live bill, why the agent didn't push it on its own.
 * Null when there is nothing to explain. Only one is shown: when a person
 * can't push, why the agent didn't is beside the point.
 */
export const quickbooksPushNote = (
  status: ApQuickbooksBillPushStatus,
  bill: ApQuickbooksBill | null | undefined,
): string | null => {
  if (!status.pushable) return status.reason ? asSentence(status.reason) : null;
  if (
    !status.automatic_skip_reason ||
    quickbooksPushMode(status, bill) === "update"
  )
    return null;
  return `Not pushed automatically: ${asSentence(status.automatic_skip_reason)}`;
};

/**
 * The toast after a push. An approved bill can wait on the invoice's other
 * actions or a reconnect; it goes on its own once that clears, so say so.
 */
export const quickbooksPushToast = (
  action: Pick<ApAgentAction, "action_type" | "waiting_reason">,
): { title: string; description?: string } => {
  const update = action.action_type === AP_ACTION_TYPE.updateQboBill;
  if (action.waiting_reason)
    return {
      title: update
        ? "Bill update approved for QuickBooks"
        : "Bill approved for QuickBooks",
      description: `${asSentence(action.waiting_reason)} It goes to QuickBooks on its own once that clears.`,
    };
  return {
    title: update
      ? "Bill update sent to QuickBooks"
      : "Bill sent to QuickBooks",
  };
};
